import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Tag, Trash2 } from 'lucide-react';
import { useCreatePromotion, useUpdatePromotion, type CreatePromotionInput } from '../../../api/mutations/promotion.mutations';
import type { Promotion, PromotionCondition, PromotionDiscount } from '../../../api/queries/promotion.queries';
import { useCategories, useMenuItems } from '../../../api/queries/menu.queries';
import { useBranches } from '../../../api/queries/settings.queries';

interface PromotionModalProps {
  promotion: Promotion | null;
  onClose: () => void;
  onSuccess: () => void;
}

const DAYS_OF_WEEK = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

export default function PromotionModal({ promotion, onClose, onSuccess }: PromotionModalProps) {
  const { t, i18n } = useTranslation();
  const isEditing = !!promotion;

  // Form state
  const [nameEn, setNameEn] = useState(promotion?.name?.en || '');
  const [nameTh, setNameTh] = useState(promotion?.name?.th || '');
  const [descriptionEn, setDescriptionEn] = useState(promotion?.description?.en || '');
  const [descriptionTh, setDescriptionTh] = useState(promotion?.description?.th || '');
  const [type, setType] = useState<CreatePromotionInput['type']>(promotion?.type || 'discount');
  const [conditions, setConditions] = useState<PromotionCondition[]>(promotion?.conditions || []);
  const [discount, setDiscount] = useState<PromotionDiscount>(
    promotion?.discount || { type: 'percentage', value: 10 }
  );
  const [applicableCategories, setApplicableCategories] = useState<string[]>(
    promotion?.applicableCategories || []
  );
  const [applicableItems, _setApplicableItems] = useState<string[]>(
    promotion?.applicableItems || []
  );
  const [applicableBranches, setApplicableBranches] = useState<string[]>(
    promotion?.applicableBranches || []
  );
  const [startDate, setStartDate] = useState(
    promotion?.startDate?.split('T')[0] || new Date().toISOString().split('T')[0]
  );
  const [endDate, setEndDate] = useState(
    promotion?.endDate?.split('T')[0] || ''
  );
  const [isActive, setIsActive] = useState(promotion?.isActive ?? true);
  const [priority, setPriority] = useState(promotion?.priority || 1);
  const [autoApply, setAutoApply] = useState(promotion?.autoApply ?? true);
  const [requiresCouponCode, setRequiresCouponCode] = useState(promotion?.requiresCouponCode ?? false);
  const [couponCode, setCouponCode] = useState(promotion?.couponCode || '');
  const [usageLimit, setUsageLimit] = useState<number | undefined>(promotion?.usageLimit);

  // Queries
  const { data: categories } = useCategories();
  const { data: _menuItems } = useMenuItems();
  const { data: branches } = useBranches();

  // Mutations
  const createPromotion = useCreatePromotion();
  const updatePromotion = useUpdatePromotion(promotion?._id || '');

  const getLangValue = (multiLang: any) => {
    return multiLang?.[i18n.language as 'en' | 'th'] || multiLang?.en || '';
  };

  const addCondition = (conditionType: PromotionCondition['type']) => {
    let defaultValue: any;
    switch (conditionType) {
      case 'min_quantity':
        defaultValue = 1;
        break;
      case 'min_amount':
        defaultValue = 100;
        break;
      case 'day_of_week':
        defaultValue = [];
        break;
      case 'time_range':
        defaultValue = { start: '09:00', end: '18:00' };
        break;
      default:
        defaultValue = null;
    }
    setConditions([...conditions, { type: conditionType, value: defaultValue }]);
  };

  const updateCondition = (index: number, value: any) => {
    setConditions(conditions.map((c, i) => (i === index ? { ...c, value } : c)));
  };

  const removeCondition = (index: number) => {
    setConditions(conditions.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!nameEn.trim()) {
      alert(t('promotions.nameRequired'));
      return;
    }

    if (!endDate) {
      alert(t('promotions.endDateRequired'));
      return;
    }

    if (requiresCouponCode && !couponCode.trim()) {
      alert(t('promotions.couponCodeRequired'));
      return;
    }

    const data: CreatePromotionInput = {
      name: { en: nameEn, th: nameTh || nameEn },
      description: descriptionEn ? { en: descriptionEn, th: descriptionTh || descriptionEn } : undefined,
      type,
      conditions,
      discount,
      applicableCategories: applicableCategories.length > 0 ? applicableCategories : undefined,
      applicableItems: applicableItems.length > 0 ? applicableItems : undefined,
      applicableBranches: applicableBranches.length > 0 ? applicableBranches : undefined,
      startDate: new Date(startDate).toISOString(),
      endDate: new Date(endDate).toISOString(),
      isActive,
      priority,
      autoApply,
      requiresCouponCode,
      couponCode: requiresCouponCode ? couponCode : undefined,
      usageLimit: usageLimit || undefined,
    };

    try {
      if (isEditing) {
        await updatePromotion.mutateAsync(data);
      } else {
        await createPromotion.mutateAsync(data);
      }
      onSuccess();
    } catch (error) {
      console.error('Failed to save promotion:', error);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="relative w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-lg bg-card flex flex-col">
        {/* Header */}
        <div className="border-b p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Tag className="h-5 w-5" />
            <h2 className="text-xl font-bold">
              {isEditing ? t('promotions.editPromotion') : t('promotions.createPromotion')}
            </h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-secondary rounded">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">{t('promotions.nameEn')} *</label>
                <input
                  type="text"
                  value={nameEn}
                  onChange={(e) => setNameEn(e.target.value)}
                  className="w-full rounded-lg border bg-background px-3 py-2"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('promotions.nameTh')}</label>
                <input
                  type="text"
                  value={nameTh}
                  onChange={(e) => setNameTh(e.target.value)}
                  className="w-full rounded-lg border bg-background px-3 py-2"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">{t('promotions.descriptionEn')}</label>
                <textarea
                  value={descriptionEn}
                  onChange={(e) => setDescriptionEn(e.target.value)}
                  className="w-full rounded-lg border bg-background px-3 py-2"
                  rows={2}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('promotions.descriptionTh')}</label>
                <textarea
                  value={descriptionTh}
                  onChange={(e) => setDescriptionTh(e.target.value)}
                  className="w-full rounded-lg border bg-background px-3 py-2"
                  rows={2}
                />
              </div>
            </div>

            {/* Promotion Type */}
            <div>
              <label className="block text-sm font-medium mb-2">{t('promotions.type')} *</label>
              <div className="grid grid-cols-4 gap-2">
                {(['discount', 'bundle', 'buy_x_get_y', 'seasonal'] as const).map((t_type) => (
                  <button
                    key={t_type}
                    type="button"
                    onClick={() => setType(t_type)}
                    className={`p-3 rounded-lg border text-sm ${
                      type === t_type
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'hover:bg-secondary'
                    }`}
                  >
                    {t(`promotions.${t_type}`)}
                  </button>
                ))}
              </div>
            </div>

            {/* Discount Settings */}
            <div className="p-4 rounded-lg border bg-secondary/30">
              <h3 className="text-sm font-medium mb-3">{t('promotions.discountSettings')}</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">{t('promotions.discountType')}</label>
                  <select
                    value={discount.type}
                    onChange={(e) => setDiscount({ ...discount, type: e.target.value as PromotionDiscount['type'] })}
                    className="w-full rounded-lg border bg-background px-3 py-2"
                  >
                    <option value="percentage">{t('promotions.percentage')}</option>
                    <option value="fixed_amount">{t('promotions.fixedAmount')}</option>
                    <option value="fixed_price">{t('promotions.fixedPrice')}</option>
                    <option value="buy_x_get_y">{t('promotions.buyXGetY')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">
                    {discount.type === 'percentage' ? t('promotions.percentageValue') : t('promotions.amountValue')}
                  </label>
                  <input
                    type="number"
                    value={discount.value}
                    onChange={(e) => setDiscount({ ...discount, value: parseFloat(e.target.value) || 0 })}
                    className="w-full rounded-lg border bg-background px-3 py-2"
                    min={0}
                    max={discount.type === 'percentage' ? 100 : undefined}
                  />
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">{t('promotions.maxDiscount')}</label>
                  <input
                    type="number"
                    value={discount.maxDiscount || ''}
                    onChange={(e) => setDiscount({ ...discount, maxDiscount: parseFloat(e.target.value) || undefined })}
                    className="w-full rounded-lg border bg-background px-3 py-2"
                    min={0}
                    placeholder={t('common.optional')}
                  />
                </div>
              </div>
            </div>

            {/* Conditions */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium">{t('promotions.conditions')}</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => addCondition('min_quantity')}
                    className="text-xs px-2 py-1 rounded bg-secondary hover:bg-secondary/80"
                  >
                    + {t('promotions.minQuantity')}
                  </button>
                  <button
                    type="button"
                    onClick={() => addCondition('min_amount')}
                    className="text-xs px-2 py-1 rounded bg-secondary hover:bg-secondary/80"
                  >
                    + {t('promotions.minAmount')}
                  </button>
                  <button
                    type="button"
                    onClick={() => addCondition('day_of_week')}
                    className="text-xs px-2 py-1 rounded bg-secondary hover:bg-secondary/80"
                  >
                    + {t('promotions.dayOfWeek')}
                  </button>
                  <button
                    type="button"
                    onClick={() => addCondition('time_range')}
                    className="text-xs px-2 py-1 rounded bg-secondary hover:bg-secondary/80"
                  >
                    + {t('promotions.timeRange')}
                  </button>
                </div>
              </div>

              {conditions.length > 0 && (
                <div className="space-y-2">
                  {conditions.map((condition, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 rounded-lg border">
                      <span className="text-sm font-medium min-w-32">
                        {t(`promotions.${condition.type}`)}:
                      </span>
                      {condition.type === 'min_quantity' && (
                        <input
                          type="number"
                          value={condition.value}
                          onChange={(e) => updateCondition(index, parseInt(e.target.value) || 0)}
                          className="w-24 rounded border bg-background px-2 py-1"
                          min={1}
                        />
                      )}
                      {condition.type === 'min_amount' && (
                        <div className="flex items-center gap-1">
                          <span>฿</span>
                          <input
                            type="number"
                            value={condition.value}
                            onChange={(e) => updateCondition(index, parseFloat(e.target.value) || 0)}
                            className="w-24 rounded border bg-background px-2 py-1"
                            min={0}
                          />
                        </div>
                      )}
                      {condition.type === 'day_of_week' && (
                        <div className="flex gap-1 flex-wrap">
                          {DAYS_OF_WEEK.map((day, dayIndex) => (
                            <button
                              key={day}
                              type="button"
                              onClick={() => {
                                const days = condition.value || [];
                                if (days.includes(dayIndex)) {
                                  updateCondition(index, days.filter((d: number) => d !== dayIndex));
                                } else {
                                  updateCondition(index, [...days, dayIndex]);
                                }
                              }}
                              className={`px-2 py-1 rounded text-xs ${
                                (condition.value || []).includes(dayIndex)
                                  ? 'bg-primary text-primary-foreground'
                                  : 'bg-secondary'
                              }`}
                            >
                              {t(`common.${day.slice(0, 3)}`)}
                            </button>
                          ))}
                        </div>
                      )}
                      {condition.type === 'time_range' && (
                        <div className="flex items-center gap-2">
                          <input
                            type="time"
                            value={condition.value?.start || '09:00'}
                            onChange={(e) => updateCondition(index, { ...condition.value, start: e.target.value })}
                            className="rounded border bg-background px-2 py-1"
                          />
                          <span>-</span>
                          <input
                            type="time"
                            value={condition.value?.end || '18:00'}
                            onChange={(e) => updateCondition(index, { ...condition.value, end: e.target.value })}
                            className="rounded border bg-background px-2 py-1"
                          />
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => removeCondition(index)}
                        className="ml-auto p-1 text-destructive hover:bg-destructive/10 rounded"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Applicable Categories */}
            <div>
              <label className="block text-sm font-medium mb-2">{t('promotions.applicableCategories')}</label>
              <div className="flex flex-wrap gap-2">
                {categories?.map((cat) => (
                  <button
                    key={cat._id}
                    type="button"
                    onClick={() => {
                      if (applicableCategories.includes(cat._id)) {
                        setApplicableCategories(applicableCategories.filter((id) => id !== cat._id));
                      } else {
                        setApplicableCategories([...applicableCategories, cat._id]);
                      }
                    }}
                    className={`px-3 py-1 rounded-lg text-sm ${
                      applicableCategories.includes(cat._id)
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-secondary hover:bg-secondary/80'
                    }`}
                  >
                    {getLangValue(cat.name)}
                  </button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-1">{t('promotions.leaveEmptyForAll')}</p>
            </div>

            {/* Applicable Branches */}
            <div>
              <label className="block text-sm font-medium mb-2">{t('promotions.applicableBranches')}</label>
              <div className="flex flex-wrap gap-2">
                {branches?.map((branch) => (
                  <button
                    key={branch._id}
                    type="button"
                    onClick={() => {
                      if (applicableBranches.includes(branch._id)) {
                        setApplicableBranches(applicableBranches.filter((id) => id !== branch._id));
                      } else {
                        setApplicableBranches([...applicableBranches, branch._id]);
                      }
                    }}
                    className={`px-3 py-1 rounded-lg text-sm ${
                      applicableBranches.includes(branch._id)
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-secondary hover:bg-secondary/80'
                    }`}
                  >
                    {branch.name}
                  </button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-1">{t('promotions.leaveEmptyForAll')}</p>
            </div>

            {/* Date Range */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">{t('promotions.startDate')} *</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full rounded-lg border bg-background px-3 py-2"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('promotions.endDate')} *</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full rounded-lg border bg-background px-3 py-2"
                  min={startDate}
                  required
                />
              </div>
            </div>

            {/* Settings */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">{t('promotions.priority')}</label>
                <input
                  type="number"
                  value={priority}
                  onChange={(e) => setPriority(parseInt(e.target.value) || 1)}
                  className="w-full rounded-lg border bg-background px-3 py-2"
                  min={1}
                  max={100}
                />
                <p className="text-xs text-muted-foreground mt-1">{t('promotions.priorityHint')}</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('promotions.usageLimit')}</label>
                <input
                  type="number"
                  value={usageLimit || ''}
                  onChange={(e) => setUsageLimit(parseInt(e.target.value) || undefined)}
                  className="w-full rounded-lg border bg-background px-3 py-2"
                  min={1}
                  placeholder={t('promotions.unlimited')}
                />
              </div>
            </div>

            {/* Toggles */}
            <div className="grid grid-cols-3 gap-4">
              <label className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer hover:bg-secondary/50">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className="rounded"
                />
                <div>
                  <p className="text-sm font-medium">{t('promotions.isActive')}</p>
                  <p className="text-xs text-muted-foreground">{t('promotions.isActiveHint')}</p>
                </div>
              </label>
              <label className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer hover:bg-secondary/50">
                <input
                  type="checkbox"
                  checked={autoApply}
                  onChange={(e) => setAutoApply(e.target.checked)}
                  className="rounded"
                />
                <div>
                  <p className="text-sm font-medium">{t('promotions.autoApply')}</p>
                  <p className="text-xs text-muted-foreground">{t('promotions.autoApplyHint')}</p>
                </div>
              </label>
              <label className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer hover:bg-secondary/50">
                <input
                  type="checkbox"
                  checked={requiresCouponCode}
                  onChange={(e) => setRequiresCouponCode(e.target.checked)}
                  className="rounded"
                />
                <div>
                  <p className="text-sm font-medium">{t('promotions.requiresCouponCode')}</p>
                  <p className="text-xs text-muted-foreground">{t('promotions.requiresCouponCodeHint')}</p>
                </div>
              </label>
            </div>

            {/* Coupon Code */}
            {requiresCouponCode && (
              <div>
                <label className="block text-sm font-medium mb-1">{t('promotions.couponCode')} *</label>
                <input
                  type="text"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  className="w-full max-w-xs rounded-lg border bg-background px-3 py-2 font-mono"
                  placeholder="SUMMER2024"
                  required={requiresCouponCode}
                />
              </div>
            )}
          </div>
        </form>

        {/* Footer */}
        <div className="border-t p-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg border hover:bg-secondary transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={createPromotion.isPending || updatePromotion.isPending}
            className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {(createPromotion.isPending || updatePromotion.isPending)
              ? t('common.saving')
              : isEditing
              ? t('common.save')
              : t('promotions.createPromotion')}
          </button>
        </div>
      </div>
    </div>
  );
}
